const TREATMENT_PLAN_ATTESTATIONS: string[] = [
  "This treatment plan is based on the most current and accurate clinical information for the client.",
  "I have reviewed applicable medical necessity criteria for the client's insurance plan and confirm this plan meets those requirements.",
  "All goals, interventions, and recommended treatments are individualized, clinically appropriate, and supported by assessment and data.",
  "I have reviewed all content for accuracy and completeness.",
  "I understand that portions of this plan may have been generated or supported by AI tools; I have thoroughly reviewed and validated all content to ensure clinical accuracy and appropriateness.",
];

const NINETY_DAY_PROGRESS_ATTESTATIONS: string[] = [
  "This progress summary reflects the most current and accurate clinical information for the client over the applicable 90-day reporting period.",
  "I have reviewed applicable medical necessity criteria and confirm this summary supports the continued medical necessity of ABA services for this client.",
  "All reported progress, goal status updates, and clinical recommendations are individualized, clinically appropriate, and supported by assessment and session data.",
  "I have reviewed all content for accuracy and completeness.",
  "I understand that portions of this summary may have been generated or supported by AI tools; I have thoroughly reviewed and validated all content to ensure clinical accuracy and appropriateness.",
];

const INCIDENT_REPORT_ATTESTATIONS: string[] = [
  "This incident report accurately describes the event as observed and/or reported, to the best of my knowledge at the time of documentation.",
  "All relevant details, including antecedents, behaviors, consequences, individuals involved, and actions taken, have been documented objectively and without omission of material facts.",
  "Any follow-up actions, notifications, and safety measures taken are consistent with organizational policy and regulatory requirements.",
  "I have reviewed all content for accuracy and completeness.",
  "I understand that portions of this report may have been generated or supported by AI tools; I have thoroughly reviewed and validated all content to ensure clinical accuracy and appropriateness.",
];

const HOURS_ADDENDUM_REQUEST_ATTESTATIONS: string[] = [
  "The requested modification to authorized service hours is based on the most current and accurate clinical information for the client.",
  "I have reviewed applicable medical necessity criteria for the client's insurance plan and confirm the requested hours are clinically justified and meet those requirements.",
  "The clinical rationale provided is individualized, supported by assessment and data, and reflects the client's current treatment needs.",
  "I have reviewed all content for accuracy and completeness.",
  "I understand that portions of this request may have been generated or supported by AI tools; I have thoroughly reviewed and validated all content to ensure clinical accuracy and appropriateness.",
];

const DISCHARGE_REPORT_ATTESTATIONS: string[] = [
  "This discharge report accurately reflects the client's clinical course, progress, and current status at the time of discharge.",
  "The reason for discharge, discharge disposition, and any recommendations for continued care are clinically appropriate and based on the client's individual needs.",
  "All documented progress, outcomes, and clinical summaries are supported by assessment data and session documentation.",
  "I have reviewed all content for accuracy and completeness.",
  "I understand that portions of this report may have been generated or supported by AI tools; I have thoroughly reviewed and validated all content to ensure clinical accuracy and appropriateness.",
];

export const DOCUMENT_TYPE_ATTESTATIONS: Record<string, string[]> = {
  TREATMENT_PLAN: TREATMENT_PLAN_ATTESTATIONS,
  INITIAL_TREATMENT_PLAN: TREATMENT_PLAN_ATTESTATIONS,
  RE_ASSESSMENT_TREATMENT_PLAN: TREATMENT_PLAN_ATTESTATIONS,
  "90_DAY_PROGRESS": NINETY_DAY_PROGRESS_ATTESTATIONS,
  INCIDENT_REPORT: INCIDENT_REPORT_ATTESTATIONS,
  HOURS_ADDENDUM_REQUEST: HOURS_ADDENDUM_REQUEST_ATTESTATIONS,
  DISCHARGE_REPORT: DISCHARGE_REPORT_ATTESTATIONS,
};

export type VerifierRole = "CLINICIAN" | "SUPERVISOR" | "CLIENT";

export const DOCUMENT_TYPE_VERIFIER_SEQUENCE: Record<string, VerifierRole[]> = {
  INITIAL_TREATMENT_PLAN: ["CLINICIAN", "CLIENT"],
  RE_ASSESSMENT_TREATMENT_PLAN: ["CLINICIAN", "CLIENT"],
  "90_DAY_PROGRESS": ["CLINICIAN", "CLIENT"],
  INCIDENT_REPORT: ["CLINICIAN", "SUPERVISOR"],
  HOURS_ADDENDUM_REQUEST: ["CLINICIAN"],
  DISCHARGE_REPORT: ["CLINICIAN"],
};

// Cognito group names as stored in state.user.groups
export const VERIFIER_ROLE_TO_GROUPS: Record<VerifierRole, string[]> = {
  CLINICIAN: ["Clinicians", "ClinicalSupervisor", "ClinicalDirector"],
  SUPERVISOR: ["ClinicalSupervisor", "ClinicalDirector"],
  CLIENT: ["Caregivers"],
};

export const userGroupsMatchVerifierRole = (
  groups: string[] | undefined,
  role: VerifierRole | null | undefined
): boolean => {
  if (!role || !groups?.length) return false;
  return VERIFIER_ROLE_TO_GROUPS[role]?.some((group) => groups.includes(group)) ?? false;
};
